import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, BookOpen, Target, MapPin, FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const UrbaltourPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/50"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_highland-library/artifacts/0bgj54ay_IMG_9317.jpeg)',
            backgroundPosition: 'center center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 inline-block">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">
                URBALTOUR
              </h1>
              <p className="text-2xl md:text-3xl mb-4 text-white drop-shadow-lg">
                Urbanization and Tourism in the Highlands
              </p>
              <p className="text-xl text-white leading-relaxed drop-shadow-md">
                Examining the intersections of urbanization and tourism in mountainous and highland regions across South and Southeast Asia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About the Project</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>URBALTOUR</strong> is a multi-sited research project examining the complex intersections 
                of urbanization and tourism in mountainous and highland regions across South and Southeast Asia. 
                Funded by the French National Agency for Research (ANR), the project is a collaborative initiative 
                coordinated by the French Institute of Pondicherry (IFP) in partnership with various international 
                institutions, including The Highland Institute.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                The project investigates how tourism is reshaping urban development, governance structures, and 
                socio-economic dynamics in highland contexts. Through comparative analysis across multiple sites, 
                URBALTOUR explores how tourism-driven urbanization creates new opportunities and challenges for 
                mountain communities, local economies, and environmental sustainability.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Kohima, the capital of Nagaland in Northeast India, serves as one of the primary analytical sites 
                within the larger URBALTOUR framework. The Highland Institute leads research activities in Kohima, 
                examining the city's transformation from a military and administrative center into an emerging 
                tourism hub, particularly through the lens of the internationally renowned Hornbill Festival.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Focus on Kohima */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Focus on Kohima</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Historical Transformation</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Kohima's urban trajectory reflects a remarkable transformation. Originally established as a 
                  military garrison and administrative center during British colonial rule, the city has evolved 
                  into a vibrant tourism destination, particularly following Nagaland's statehood in 1963.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The launch of the Hornbill Festival in 2000 marked a pivotal moment in Kohima's development, 
                  catalyzing infrastructure growth, hospitality sector expansion, and increased visitor footfall. 
                  Today, the city experiences an annual influx of domestic and international tourists, fundamentally 
                  reshaping its economic base, spatial organization, and social fabric.
                </p>
              </div>
              
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_highland-library/artifacts/72zfty02_View%20of%20Kohima%20in%201940s.jpg" 
                  alt="View of Kohima in 1940s"
                  className="w-full h-full object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Research Dimensions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Infrastructure & Development</h4>
                    <p className="text-gray-600 text-sm">
                      Examining the rise of homestays, hotels, and hospitality infrastructure, alongside 
                      constraints in water supply, waste management, and transportation networks.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Stakeholder Engagement</h4>
                    <p className="text-gray-600 text-sm">
                      Interviews with government departments, village councils, hospitality sector operators, 
                      and tourists to understand diverse perspectives and decision-making processes.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Globe className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Environmental Pressures</h4>
                    <p className="text-gray-600 text-sm">
                      Analyzing the ecological impacts of tourism growth, including waste generation, 
                      water scarcity, traffic congestion, and strain on natural resources.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Target className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Sustainable Tourism</h4>
                    <p className="text-gray-600 text-sm">
                      Exploring pathways toward year-round, sustainable tourism strategies that reduce 
                      dependence on the Hornbill Festival and create equitable economic opportunities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Themes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Research Themes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>New Urban Models</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Tourism as a catalyst for innovative urban development patterns, spatial configurations, 
                  and regional connectivity in highland contexts
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Governance & Planning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Transformations in governance structures, public action frameworks, and urban planning 
                  processes shaped by tourism development
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Hospitality Dynamics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Analysis of hospitality sector growth, market dynamics, service quality standards, 
                  and socio-economic inequalities
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Environmental Impact</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Social and environmental consequences of rapid tourism growth, including resource 
                  management and ecological sustainability
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Grassroots Participation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Livelihoods, entrepreneurship opportunities, and women-led initiatives in the tourism 
                  and hospitality sectors
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <ArrowRight className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Urban-Rural Linkages</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Relationships between Kohima and surrounding villages, examining flows of people, 
                  resources, and cultural practices
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Methods */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Research Methods</h2>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                The URBALTOUR project in Kohima employs a multi-method approach combining qualitative and 
                quantitative research techniques to capture the complexity of tourism-driven urbanization:
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">1</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Semi-Structured Interviews</h4>
                    <p className="text-gray-600">
                      In-depth conversations with government officials, municipal authorities, village council 
                      representatives, hospitality sector operators, and community stakeholders to understand 
                      decision-making processes, challenges, and aspirations.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">2</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Surveys & Questionnaires</h4>
                    <p className="text-gray-600">
                      Systematic data collection from hotels, homestays, restaurants, vendors, and tourists 
                      to quantify service provision, visitor profiles, economic flows, and satisfaction levels.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">3</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">GIS-Based Spatial Analysis</h4>
                    <p className="text-gray-600">
                      Geographic information systems mapping of tourism infrastructure, hospitality facilities, 
                      transportation networks, and urban growth patterns to visualize spatial transformations.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">4</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Multi-Year Fieldwork</h4>
                    <p className="text-gray-600">
                      Longitudinal research activities conducted between 2022 and 2025, allowing for temporal 
                      analysis of changes, seasonal variations, and evolving dynamics in Kohima's tourism sector.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Research Team</h2>
          
          <div className="max-w-4xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">International Lead</h3>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Dr. Bertrand Lefebvre</h4>
                    <p className="text-green-600 font-medium mb-3">Project Lead & Principal Investigator</p>
                    <p className="text-gray-700 mb-2">
                      <strong>French Institute of Pondicherry (IFP)</strong>
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Dr. Lefebvre is a geographer and urban researcher specializing in South and Southeast Asian 
                      urbanization processes. He coordinates the multi-sited URBALTOUR project and leads comparative 
                      research on tourism-driven urban transformations across highland regions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Highland Institute Team (Kohima Site)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Rovithono Yhome</h4>
                  <p className="text-green-600 font-medium mb-3">Senior Researcher</p>
                  <p className="text-gray-700 mb-2">
                    <strong>The Highland Institute</strong>
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Rovithono Yhome leads field research activities in Kohima, conducting stakeholder interviews, 
                    coordinating surveys, and analyzing urban governance and planning dimensions of tourism development. 
                    Her work focuses on municipal responses to tourism growth and infrastructure challenges.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Akumtong Imchen</h4>
                  <p className="text-green-600 font-medium mb-3">Researcher</p>
                  <p className="text-gray-700 mb-2">
                    <strong>The Highland Institute</strong>
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Akumtong Imchen contributes to data collection, spatial mapping, and analysis of hospitality 
                    sector dynamics. His research examines the role of homestays, women-led entrepreneurship, and 
                    community participation in Kohima's tourism economy.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-600 text-sm">
                The Highland Institute team is supported by administrative staff, field assistants, and research 
                associates who contribute to survey implementation, data processing, and logistical coordination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Project Partners</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">French Institute of Pondicherry (IFP)</h4>
                  <p className="text-gray-600 text-sm">
                    Lead institution coordinating the multi-sited URBALTOUR research project across South and 
                    Southeast Asia
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">The Highland Institute</h4>
                  <p className="text-gray-600 text-sm">
                    Research partner leading field activities, stakeholder engagement, and data collection in 
                    Kohima, Nagaland
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">French National Research Agency (ANR)</h4>
                  <p className="text-gray-600 text-sm">
                    Primary funding agency supporting the URBALTOUR research initiative
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Local Stakeholders (Kohima)</h4>
                  <p className="text-gray-600 text-sm">
                    Municipal authorities, village councils, hospitality sector operators, and community 
                    organizations collaborating in research activities
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Publications & Outputs</h2>
            
            <div className="space-y-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        URBALTOUR: Examining the Rise of Tourism in Kohima, Nagaland
                      </h4>
                      <p className="text-gray-600 text-sm mb-3">
                        <em>The Highlander: A Quarterly Review of Highland Asia</em> (2024)
                      </p>
                      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                        Comprehensive research article analyzing tourism development patterns, governance challenges, 
                        and socio-economic transformations in Kohima. Based on multi-year fieldwork and stakeholder 
                        engagement.
                      </p>
                      <a 
                        href="https://zenodo.org/records/11002297"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        Read Full Article
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        On the Field! Kohima with Bertrand Lefebvre, Akumtong Imchen & Rovithono Yhome
                      </h4>
                      <p className="text-gray-600 text-sm mb-3">
                        <em>URBALTOUR Field Report Blog</em>
                      </p>
                      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                        Field report documenting research activities, methodological approaches, and preliminary 
                        findings from collaborative fieldwork in Kohima. Includes reflections on stakeholder 
                        engagement and research challenges.
                      </p>
                      <a 
                        href="https://doi.org/10.58079/vfhx"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        Read Field Report
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Globe className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        URBALTOUR Project Website
                      </h4>
                      <p className="text-gray-600 text-sm mb-3">
                        <em>Project Hub & Research Updates</em>
                      </p>
                      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                        Official URBALTOUR project website featuring updates from all research sites, working papers, 
                        multimedia content, and project news. Includes comparative insights from highland cities 
                        across South and Southeast Asia.
                      </p>
                      <a 
                        href="https://urbaltour.hypotheses.org/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        Visit Project Website
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-600 text-sm">
                <strong>Note:</strong> Additional publications, working papers, policy briefs, and research outputs 
                will be added as the project progresses through 2025. For the latest updates, visit the 
                <a 
                  href="https://urbaltour.hypotheses.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 font-medium ml-1"
                >
                  URBALTOUR project website
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-900 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Learn More</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Explore our other research projects or get in touch to learn more about URBALTOUR
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/research">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                All Research Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UrbaltourPage;
